import React, { useEffect, useState } from 'react';
import { actualizarLogisticaTramite } from '../../services/tramiteService';
import { formatearMesAnio } from '../../utils/fechaLiquidar';
import { useSincronizarFecha } from './CampoFechaLiquidar';

/** Año fuera de este rango = todavía se está tipeando, no es una fecha para guardar. */
const ANIO_MINIMO = 1900;
const ANIO_MAXIMO = 2100;

function esFechaUsable(valor) {
    if (!valor) return true; // vacío es válido: significa sacarle la fecha
    const anio = Number(String(valor).slice(0, 4));
    return Number.isInteger(anio) && anio >= ANIO_MINIMO && anio <= ANIO_MAXIMO;
}

/**
 * La fecha de liquidar del trámite (tramite.fechaInicioEstimada), en su propia tarjeta.
 *
 * POR QUÉ ESTÁ SEPARADA DE LA CARPETA
 * Antes vivía dentro de PanelUbicacionTramite, y el mismo dato tenía DOS editores: uno suelto en
 * la vista y otro adentro del modo "Editar", los dos escribiendo el mismo estado. Editar en uno
 * pisaba al otro. Son dos datos que no tienen nada que ver entre sí —dónde está el papel y cuándo
 * se liquida— y ahora cada uno tiene su tarjeta y su guardado.
 *
 * POR QUÉ EL INPUT ES NO CONTROLADO
 * Con `value={...}` el campo se rompía al tipear el año ("solo deja poner un carácter"). Está
 * explicado en useSincronizarFecha, que es lo que se usa acá: el navegador maneja sus segmentos y
 * solo se le escribe el valor cuando cambia lo GUARDADO. El borrador se sigue en estado aparte,
 * nada más que para saber si hay cambios sin guardar.
 *
 * POR QUÉ NO GUARDA SOLO
 * Tampoco guarda en cada tecla, como hacía antes: cada pulsación disparaba un PATCH y el refresco
 * del padre reseteaba el campo a mitad de la escritura. Se confirma con el botón.
 */
export default function TarjetaFechaLiquidar({ tramite, onUpdated }) {
    const guardada = tramite?.fechaInicioEstimada || '';
    const [borrador, setBorrador] = useState(guardada);
    const [guardando, setGuardando] = useState(false);
    const [error, setError] = useState('');
    const inputRef = useSincronizarFecha(guardada);

    // Depende del VALOR guardado y del id, no del objeto `tramite`: la ficha lo reconstruye en
    // cada refresco, y un efecto sobre su identidad volvía a pisar lo que se estaba escribiendo.
    useEffect(() => {
        setBorrador(guardada);
        setError('');
    }, [tramite?.idTramite, guardada]);

    if (!tramite?.idTramite) return null;

    const hayCambios = borrador !== guardada;

    const guardar = async (valorCrudo) => {
        const valor = valorCrudo || null;
        if (!esFechaUsable(valor)) {
            setError('Revisá el año.');
            return;
        }
        setGuardando(true);
        setError('');
        try {
            const actualizado = await actualizarLogisticaTramite(tramite.idTramite, {
                // El flag es derivado de la fecha en todo el sistema: nunca uno sin el otro.
                iniciarDiferido: !!valor,
                fechaInicioEstimada: valor,
            });
            if (onUpdated) onUpdated((prev) => ({
                ...prev,
                iniciarDiferido: actualizado.iniciarDiferido,
                fechaInicioEstimada: actualizado.fechaInicioEstimada,
            }));
        } catch (e) {
            setBorrador(guardada);
            // `guardada` no cambió, así que useSincronizarFecha no va a reescribir el campo solo.
            if (inputRef.current) inputRef.current.value = guardada;
            // El endpoint responde el motivo como texto plano (ej. año inválido), no como objeto.
            const detalle = e?.response?.data;
            setError((typeof detalle === 'string' && detalle) || detalle?.message || 'No se pudo guardar la fecha.');
        } finally {
            setGuardando(false);
        }
    };

    const tarjeta = {
        // Una franja baja, como la de carpeta: todo en una línea (y baja a otra solo si no entra).
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10,
        background: guardada ? '#fffbeb' : '#f8fafc',
        border: `1px solid ${guardada ? '#fde68a' : '#e2e8f0'}`,
        borderRadius: 8, padding: '8px 12px', fontSize: '0.9rem',
        // Comparte fila con la de carpeta. La base es lo que ocupa en una línea (título, fecha,
        // botón y mes): si no entra al lado, baja entera en vez de partirse en varias líneas.
        flex: '2 1 460px', minWidth: 250,
    };

    return (
        <div style={tarjeta}>
            <span style={{ color: '#334155', fontWeight: 600, fontSize: '0.9rem', whiteSpace: 'nowrap' }}>
                <i className="bi bi-clock-history me-1" style={{ color: '#b45309' }} />
                Fecha de liquidar (LIQ):
            </span>

            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <input
                    type="date"
                    // No controlado a propósito, para que se pueda tipear el año: ver el
                    // comentario del componente y useSincronizarFecha.
                    ref={inputRef}
                    defaultValue={guardada}
                    disabled={guardando}
                    min={`${ANIO_MINIMO}-01-01`}
                    max={`${ANIO_MAXIMO}-12-31`}
                    onChange={(e) => setBorrador(e.target.value)}
                    style={{ borderRadius: 5, padding: '2px 6px',
                        fontSize: '0.85rem', background: '#fff', color: '#334155' }}
                />
                {hayCambios && (
                    <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        style={{ padding: '1px 10px', fontSize: '0.8rem', fontWeight: 600 }}
                        onClick={() => guardar(borrador)}
                        disabled={guardando}
                    >
                        {guardando ? 'Guardando…' : 'Guardar'}
                    </button>
                )}
                {!hayCambios && guardada && (
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        style={{ padding: '1px 10px', fontSize: '0.8rem' }}
                        onClick={() => guardar(null)}
                        disabled={guardando}
                        title="El trámite deja de esperar un mes"
                    >
                        Quitar
                    </button>
                )}
            </div>

            {/* Solo el mes: es como se lee A liquidar. La explicación larga va en el title. */}
            <span
                style={{ color: '#334155', fontSize: '0.85rem', fontWeight: 600, whiteSpace: 'nowrap' }}
                title={guardada
                    ? 'Queda en «A liquidar» esperando ese mes.'
                    : 'Sin fecha: la carpeta aparece en «A liquidar» sin mes asignado.'}
            >
                {guardada ? formatearMesAnio(guardada) : 'Sin mes asignado'}
            </span>

            {error && (
                <div className="alert alert-danger py-1 px-2 mb-0" style={{ fontSize: '0.78rem', width: '100%' }}>
                    {error}
                </div>
            )}
        </div>
    );
}
